import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function OrdersSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { labels: expLabels } = useExpenditureLabels();

  const placeholderVariables = [
    { var: '<<Company Name>>', desc: 'Your company name' },
    { var: '<<Company Address>>', desc: 'Your company address' },
    { var: '<<Order Number>>', desc: 'Purchase order number' },
    { var: '<<Order Date>>', desc: 'Date of the order' },
    { var: '<<Supplier Name>>', desc: 'Supplier company name' },
    { var: '<<Supplier Address>>', desc: 'Supplier address' },
    { var: '<<Delivery Address>>', desc: 'Delivery location address' },
    { var: '<<Buyer Name>>', desc: 'Name of the buyer' },
    { var: '<<Buyer Email>>', desc: 'Email of the buyer' },
    { var: '<<Total Amount>>', desc: 'Order total amount' },
    { var: '<<Currency>>', desc: 'Currency code (USD, GBP, etc.)' },
    { var: '<<Payment Terms>>', desc: 'Payment terms text' },
    { var: '<<Delivery Terms>>', desc: 'Delivery/shipping terms' },
  ];

  const orderHeaderFields = [
    { id: 'account', label: expLabels.exp_code_1 || 'Account', icon: '📊' },
    { id: 'budget', label: 'Budget', icon: '💵' },
    { id: 'category_code', label: 'Category Code', icon: '📁' },
    { id: 'contract_number', label: 'Contract Number', icon: '📋' },
    { id: 'delivery_code', label: 'Delivery Code', icon: '📦' },
    { id: 'delivery_to', label: 'Delivery To', icon: '📍' },
    { id: 'department_code', label: expLabels.exp_code_3 || 'Department Code', icon: '🏢' },
    { id: 'document_type', label: 'Document Type', icon: '📄' },
    { id: 'incoterms', label: 'Incoterms', icon: '🚢' },
    { id: 'instructions', label: 'Instructions', icon: '📝' },
    { id: 'invoice_code', label: 'Invoice Code', icon: '🧾' },
    { id: 'justification', label: 'Justification', icon: '✍️' },
    { id: 'payment_terms', label: 'Payment Terms', icon: '💳' },
    { id: 'ref_num', label: 'Ref Num', icon: '🔢' },
    { id: 'restaurant', label: expLabels.exp_code_2 || 'Restaurant', icon: '🍽️' },
    { id: 'ship_via', label: 'Ship Via', icon: '🚚' },
    { id: 'supplier_contact', label: 'Supplier Contact', icon: '👤' },
  ];

  const orderLineFields = [
    { id: 'due_date', label: 'Due Date', icon: '📅' },
    { id: 'gl', label: expLabels.exp_code_4 || 'GL', icon: '📒' },
    { id: 'nominal_code', label: expLabels.exp_code_6 || 'Nominal Code', icon: '🔢' },
    { id: 'property_code', label: expLabels.exp_code_5 || 'Property Code', icon: '🏠' },
    { id: 'suppliers_part_number', label: 'Suppliers Part Number', icon: '🔩' },
    { id: 'unit', label: 'Unit', icon: '📏' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/order-settings');
      setSettings(res.data.settings || getDefaultSettings());
    } catch (err) {
      setSettings(getDefaultSettings());
    }
    setLoading(false);
  };

  const getDefaultSettings = () => ({
    order_header_text: '',
    order_footer_text: '',
    order_email_subject: 'Purchase Order <<Order Number>> from <<Company Name>>',
    order_email_body: '',
    budget_deduction_status: 'issued',
    excess_tolerance_amendment_type: '%',
    excess_tolerance_amendment_value: 0,
    excess_tolerance_approved_type: '%',
    excess_tolerance_approved_value: 0,
    excess_tolerance_gr_type: '%',
    excess_tolerance_gr_value: 0,
    excess_tolerance_invoice_type: '%',
    excess_tolerance_invoice_value: 0,
    mandatory_fields: { header: [], line: [] }
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/order-settings', settings);
      toast.success('Order settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const toggleMandatoryField = (section, field) => {
    setSettings(prev => {
      const fields = prev.mandatory_fields?.[section] || [];
      const newFields = fields.includes(field)
        ? fields.filter(f => f !== field)
        : [...fields, field];
      return {
        ...prev,
        mandatory_fields: { ...prev.mandatory_fields, [section]: newFields }
      };
    });
  };

  const toleranceRow = (label, typeKey, valueKey) => (
    <div className="flex items-center gap-4 py-3" key={typeKey}>
      <span className="w-64 text-sm">{label}</span>
      <select
        value={settings[typeKey] || '%'}
        onChange={(e) => setSettings(p => ({ ...p, [typeKey]: e.target.value }))}
        className="w-24 h-9 rounded-md border border-input bg-background px-3 text-sm"
        data-testid={`order-tolerance-${typeKey}`}
      >
        <option value="%">%</option>
        <option value="value">Value</option>
      </select>
      <Input
        type="number"
        step="0.01"
        value={settings[valueKey] || 0}
        onChange={(e) => setSettings(p => ({ ...p, [valueKey]: parseFloat(e.target.value) || 0 }))}
        className="w-28 text-right"
        data-testid={`order-tolerance-${valueKey}`}
      />
    </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl" data-testid="orders-settings-panel">
      <h1 className="text-2xl font-bold mb-1">Orders</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Order Settings</p>

      {/* Section 1: Order Text */}
      <div className="border rounded-lg p-6 mb-6" data-testid="order-text-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Order Text
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Define the text that appears on purchase orders and in order emails.
          Use placeholder variables to insert dynamic values.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Order Header Text</Label>
              <Textarea
                value={settings.order_header_text || ''}
                onChange={(e) => setSettings(p => ({ ...p, order_header_text: e.target.value }))}
                rows={3}
                placeholder="Enter header text for purchase orders..."
                className="font-mono text-sm"
                data-testid="order-header-text"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Order Footer Text</Label>
              <Textarea
                value={settings.order_footer_text || ''}
                onChange={(e) => setSettings(p => ({ ...p, order_footer_text: e.target.value }))}
                rows={3}
                placeholder="Enter footer text for purchase orders..."
                className="font-mono text-sm"
                data-testid="order-footer-text"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Order Email Subject</Label>
              <Input
                value={settings.order_email_subject || ''}
                onChange={(e) => setSettings(p => ({ ...p, order_email_subject: e.target.value }))}
                placeholder="Email subject line..."
                className="font-mono text-sm"
                data-testid="order-email-subject"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Order Email Body</Label>
              <Textarea
                value={settings.order_email_body || ''}
                onChange={(e) => setSettings(p => ({ ...p, order_email_body: e.target.value }))}
                rows={4}
                placeholder="Enter email body text..."
                className="font-mono text-sm"
                data-testid="order-email-body"
              />
            </div>
          </div>
          <div className="border rounded-lg p-4 bg-muted/20 h-fit">
            <h4 className="font-semibold text-xs mb-2">Placeholder Variables</h4>
            <p className="text-[10px] text-muted-foreground mb-3">Click to copy, then paste into text field.</p>
            <div className="space-y-1">
              {placeholderVariables.map(pv => (
                <button
                  key={pv.var}
                  onClick={() => { navigator.clipboard.writeText(pv.var); toast.success(`Copied: ${pv.var}`); }}
                  className="w-full text-left px-2 py-1 rounded hover:bg-accent text-[11px]"
                  title={pv.desc}
                  data-testid={`placeholder-${pv.var.replace(/[<> ]/g, '')}`}
                >
                  <code className="text-primary font-mono">{pv.var}</code>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Set Budget Status */}
      <div className="border rounded-lg p-6 mb-6" data-testid="budget-status-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          Set Budget Status
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Select the point in the lifecycle of a purchase where your budgets should be deducted
          with each newly raised purchase order's value.
        </p>
        <div className="flex items-start gap-8">
          <Label className="w-64 text-sm pt-2">Order Status for Budget Deduction</Label>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="budget_status"
                value="approved"
                checked={settings.budget_deduction_status === 'approved'}
                onChange={(e) => setSettings(p => ({ ...p, budget_deduction_status: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="budget-status-approved"
              />
              <span className="text-sm">Deduct at final Approval (Status 'APPROVED')</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="budget_status"
                value="issued"
                checked={settings.budget_deduction_status === 'issued'}
                onChange={(e) => setSettings(p => ({ ...p, budget_deduction_status: e.target.value }))}
                className="h-4 w-4 text-primary"
                data-testid="budget-status-issued"
              />
              <span className="text-sm">Deduct at issuance to supplier (Status 'ISSUED')</span>
            </label>
          </div>
        </div>
      </div>

      {/* Section 3: Set Excess Tolerance */}
      <div className="border rounded-lg p-6 mb-6" data-testid="excess-tolerance-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Set Excess Tolerance
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Value of Order after being APPROVED / AMENDED cannot exceed more than the Excess Tolerance (expressed as a percentage or value).
        </p>
        <div className="space-y-1 border-b pb-4 mb-4">
          {toleranceRow('Excess tolerance at AMENDMENT Status', 'excess_tolerance_amendment_type', 'excess_tolerance_amendment_value')}
          {toleranceRow('Excess tolerance at APPROVED Status', 'excess_tolerance_approved_type', 'excess_tolerance_approved_value')}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          <strong>Note:</strong> Quantity of each Line Item after being ISSUED cannot exceed more than the below mentioned Excess Tolerance.
        </p>
        <div className="space-y-1">
          {toleranceRow('Excess tolerance at GR', 'excess_tolerance_gr_type', 'excess_tolerance_gr_value')}
          {toleranceRow('Excess tolerance at Invoice', 'excess_tolerance_invoice_type', 'excess_tolerance_invoice_value')}
        </div>
      </div>

      {/* Section 4: Set Mandatory Fields */}
      <div className="border rounded-lg p-6 mb-6" data-testid="order-mandatory-fields-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">4</span>
          Set Mandatory Fields
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Tick the appropriate checkbox to stipulate that additional fields are mandatory and must have a value before issuing an order.
        </p>
        <div className="grid grid-cols-2 gap-8">
          {/* Order Header */}
          <div>
            <h4 className="font-medium text-xs mb-3 text-muted-foreground uppercase tracking-wide">Order Header</h4>
            <div className="grid grid-cols-4 gap-2">
              {orderHeaderFields.map(field => {
                const isSelected = (settings.mandatory_fields?.header || []).includes(field.id);
                return (
                  <button
                    key={field.id}
                    onClick={() => toggleMandatoryField('header', field.id)}
                    className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                    }`}
                    data-testid={`order-mandatory-header-${field.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 relative ${
                      isSelected ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-[10px]">✓</span>
                        </div>
                      )}
                      <span className="text-lg">{field.icon}</span>
                    </div>
                    <span className="text-[10px] text-center leading-tight">{field.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Order Line */}
          <div>
            <h4 className="font-medium text-xs mb-3 text-muted-foreground uppercase tracking-wide">Order Line</h4>
            <div className="grid grid-cols-4 gap-2">
              {orderLineFields.map(field => {
                const isSelected = (settings.mandatory_fields?.line || []).includes(field.id);
                return (
                  <button
                    key={field.id}
                    onClick={() => toggleMandatoryField('line', field.id)}
                    className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                    }`}
                    data-testid={`order-mandatory-line-${field.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 relative ${
                      isSelected ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-[10px]">✓</span>
                        </div>
                      )}
                      <span className="text-lg">{field.icon}</span>
                    </div>
                    <span className="text-[10px] text-center leading-tight">{field.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="order-settings-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
